import type { Media, PutAdminV1OrgProductsIdMediaBody } from '@nucleus-commerce/api-client/admin';

/** One file of a product's gallery as the editor holds it. */
export interface GalleryItem {
  mediaId: string;
  /** Empty means every variant. */
  variantIds: string[];
  media: Media;
}

/** Images and videos; the product gallery takes no documents or 3D files. */
export const GALLERY_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
  'video/mp4',
  'video/webm',
] as const;

export const isGalleryFile = (file: File) =>
  (GALLERY_TYPES as readonly string[]).includes(file.type);

/**
 * The first file is the featured one, as in Shopify. A product saved before that rule may name
 * another file; it is shown first so the large tile matches what the storefront features, and the
 * next save makes the order agree.
 */
export function featuredFirst(items: GalleryItem[], featuredId: string | null | undefined) {
  const index = featuredId ? items.findIndex((i) => i.mediaId === featuredId) : -1;
  const featured = items[index];
  if (index <= 0 || !featured) return items;
  return [featured, ...items.slice(0, index), ...items.slice(index + 1)];
}

/** Moves `activeId` to where `overId` sits, shifting the files between them. */
export function moveItem(items: GalleryItem[], activeId: string, overId: string) {
  const from = items.findIndex((i) => i.mediaId === activeId);
  const to = items.findIndex((i) => i.mediaId === overId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...items];
  const [row] = next.splice(from, 1);
  if (!row) return items;
  next.splice(to, 0, row);
  return next;
}

export const moveToFront = (items: GalleryItem[], id: string) =>
  items[0] ? moveItem(items, id, items[0].mediaId) : items;

export const removeItems = (items: GalleryItem[], ids: ReadonlySet<string>) =>
  items.filter((i) => !ids.has(i.mediaId));

export const setVariantScope = (
  items: GalleryItem[],
  ids: ReadonlySet<string>,
  variantIds: string[],
) => items.map((i) => (ids.has(i.mediaId) ? { ...i, variantIds: [...variantIds] } : i));

/** The variants behind one value of an option: what a "Show for variants" chip stands for. */
export interface VariantGroup {
  value: string;
  label: string;
  variantIds: string[];
}

/** One group per value of the option, in the option's order; values without a variant drop out. */
export function variantGroups(
  option:
    { key: string; values: ReadonlyArray<{ value: string; label: string }> } | null | undefined,
  variants: ReadonlyArray<{ id: string; optionValues: Readonly<Record<string, string>> }>,
): VariantGroup[] {
  if (!option) return [];
  return option.values
    .map((value) => ({
      value: value.value,
      label: value.label,
      variantIds: variants
        .filter((variant) => variant.optionValues[option.key] === value.value)
        .map((variant) => variant.id),
    }))
    .filter((group) => group.variantIds.length > 0);
}

export const isGroupOn = (scope: readonly string[], group: VariantGroup) =>
  group.variantIds.every((id) => scope.includes(id));

/**
 * Turns a group on or off in a file's scope. Every variant ticked is the same as no scope at all,
 * and saying so keeps a variant added later showing the file.
 */
export function toggleVariantGroup(
  scope: readonly string[],
  group: VariantGroup,
  variantCount: number,
): string[] {
  const next = new Set(scope);
  const on = isGroupOn(scope, group);
  for (const id of group.variantIds) {
    if (on) next.delete(id);
    else next.add(id);
  }
  return next.size >= variantCount ? [] : [...next];
}

/** The scope holds a variant whose group is only partly ticked, so the chips can't show it. */
export function isPartialScope(scope: readonly string[], groups: readonly VariantGroup[]) {
  const whole = new Set(groups.filter((g) => isGroupOn(scope, g)).flatMap((g) => g.variantIds));
  return scope.some((id) => !whole.has(id));
}

/**
 * Undo of a removal: the removed files go back to their old places among whatever the gallery
 * holds now, so a change made after the removal (an upload, a reorder) is kept.
 */
export function restoreRemoved(
  current: GalleryItem[],
  previous: GalleryItem[],
  removed: ReadonlySet<string>,
) {
  const now = new Map(current.map((i) => [i.mediaId, i]));
  const before = new Set(previous.map((i) => i.mediaId));
  const restored = previous
    .filter((i) => now.has(i.mediaId) || removed.has(i.mediaId))
    .map((i) => now.get(i.mediaId) ?? i);
  return [...restored, ...current.filter((i) => !before.has(i.mediaId))];
}

/** Every id from `anchorId` to `targetId`, both included, in gallery order (shift-click). */
export function idsBetween(items: GalleryItem[], anchorId: string, targetId: string) {
  const a = items.findIndex((i) => i.mediaId === anchorId);
  const b = items.findIndex((i) => i.mediaId === targetId);
  if (a < 0 || b < 0) return [targetId];
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return items.slice(lo, hi + 1).map((i) => i.mediaId);
}

/** Same files, same order, same variant scopes: saving `a` over `b` would change nothing. */
export function sameGallery(a: GalleryItem[], b: GalleryItem[]) {
  return (
    a.length === b.length &&
    a.every((item, index) => {
      const other = b[index];
      if (other?.mediaId !== item.mediaId) return false;
      if (other.variantIds.length !== item.variantIds.length) return false;
      const scope = new Set(other.variantIds);
      return item.variantIds.every((id) => scope.has(id));
    })
  );
}

export function toPutBody(items: GalleryItem[]): PutAdminV1OrgProductsIdMediaBody {
  return {
    items: items.map(({ mediaId, variantIds }) => ({ mediaId, variantIds })),
    featuredMediaId: items[0]?.mediaId ?? null,
  };
}
